// React, Next
import { useContext, useState } from 'react'
import { useTranslation } from 'next-i18next'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

// Custom
import Section from '@/components/sections/Section'
import { GlobalContext } from 'context/GlobalContextProvider'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBody, getFontBodySmall, getFontH2 } from '@/assets/theme/fonts'

export default function FaqSection() {
  const { t } = useTranslation('servicesOtCybersecurity')
  const { screenDimensions } = useContext(GlobalContext)

  const mobileMode = screenDimensions.width < breakpoints.desktopToMobile

  // Only one answer stays open at a time, so the list never grows unwieldy.
  const [expanded, setExpanded] = useState<string | false>(false)

  const items = [
    {
      id: '01',
      question: t('faq.items.01.question'),
      answer: t('faq.items.01.answer')
    },
    {
      id: '02',
      question: t('faq.items.02.question'),
      answer: t('faq.items.02.answer')
    },
    {
      id: '03',
      question: t('faq.items.03.question'),
      answer: t('faq.items.03.answer')
    },
    {
      id: '04',
      question: t('faq.items.04.question'),
      answer: t('faq.items.04.answer')
    },
    {
      id: '05',
      question: t('faq.items.05.question'),
      answer: t('faq.items.05.answer')
    },
    {
      id: '06',
      question: t('faq.items.06.question'),
      answer: t('faq.items.06.answer')
    },
    {
      id: '07',
      question: t('faq.items.07.question'),
      answer: t('faq.items.07.answer')
    },
    {
      id: '08',
      question: t('faq.items.08.question'),
      answer: t('faq.items.08.answer')
    },
    {
      id: '09',
      question: t('faq.items.09.question'),
      answer: t('faq.items.09.answer')
    },
    {
      id: '10',
      question: t('faq.items.10.question'),
      answer: t('faq.items.10.answer')
    },
    {
      id: '11',
      question: t('faq.items.11.question'),
      answer: t('faq.items.11.answer')
    },
    {
      id: '12',
      question: t('faq.items.12.question'),
      answer: t('faq.items.12.answer')
    }
  ]

  const getItem = (item: { id: string; question: string; answer: string }) => (
    <Accordion
      key={item.id}
      disableGutters
      elevation={0}
      square={false}
      expanded={expanded === item.id}
      onChange={(_, isExpanded) => setExpanded(isExpanded ? item.id : false)}
      sx={{
        background: themeColors.kroemtech.background,
        border: `1px solid ${themeColors.gray.lighter}`,
        borderRadius: '10px !important',
        marginBottom: '0.75rem',
        overflow: 'hidden',
        '&::before': { display: 'none' }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: themeColors.success.main }} />}
        sx={{
          padding: mobileMode ? '0.25rem 1rem' : '0.5rem 1.5rem',
          '& .MuiAccordionSummary-content': {
            margin: '0.9rem 0'
          }
        }}
      >
        <Typography
          component="h3"
          sx={{
            fontSize: getFontBody(),
            fontWeight: 600,
            lineHeight: 1.4,
            color: themeColors.kroemtech.dunkelblau,
            paddingRight: '0.75rem'
          }}
        >
          {item.question}
        </Typography>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          padding: mobileMode ? '0 1rem 1.25rem' : '0 3.5rem 1.5rem 1.5rem',
          borderTop: `1px solid ${themeColors.gray.lighter}`,
          paddingTop: '1.25rem'
        }}
      >
        <Typography
          sx={{
            fontSize: getFontBodySmall(),
            lineHeight: 1.7,
            color: themeColors.gray.defaultText
          }}
        >
          {item.answer}
        </Typography>
      </AccordionDetails>
    </Accordion>
  )

  const getContent = () => (
    <Box sx={{ width: '100%' }}>
      <Typography
        sx={{
          fontSize: getFontBodySmall(),
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: themeColors.success.main,
          marginBottom: '0.5rem'
        }}
      >
        {t('faq.eyebrow')}
      </Typography>
      <Box
        sx={{
          width: '2.5rem',
          borderTop: `3px solid ${themeColors.success.main}`,
          marginBottom: '1.5rem'
        }}
      />

      <Typography
        variant="h2"
        component="h2"
        fontSize={getFontH2()}
        sx={{
          fontWeight: 700,
          color: themeColors.kroemtech.dunkelblau,
          marginBottom: '2.5rem'
        }}
      >
        {t('faq.title')}
      </Typography>

      <Box sx={{ maxWidth: '52rem' }}>{items.map(item => getItem(item))}</Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section background="#ffffff" paddingBottom="5rem" content={getContent()} />
  )
}
